<script setup lang="ts">
import { onMounted, reactive } from 'vue'
import { NButton, NEmpty, NInput, NPopconfirm, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import StatusPill from '../components/StatusPill.vue'
import { useCalendarCredentialsStore, type CalendarProviderStatus } from '../stores/calendar_credentials'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only Calendar page. Today it is the connection panel for the
// calendar heartbeat's OAuth credentials (the week agenda in the design has
// no data source yet, so it is not drawn).
// Per provider: set the client id/secret (write-only — the server returns only
// set/not-set), kick off the consent flow, see connection + poll health, and
// disconnect. Auth gating is at the router level; non-admins never reach here.
const store = useCalendarCredentialsStore()
const message = useMessage()
const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()
useDocumentTitle('Calendar')

// The browser OAuth consent route is provider-specific; only Google ships
// today. This map can be extended as providers are added.
const CONNECT_PATHS: Record<string, string> = { google: '/oauth/google_calendar' }

// Per-provider draft inputs (write-only) + in-flight flag, lazily created.
interface Draft {
  clientId: string
  clientSecret: string
  saving: boolean
}
const drafts = reactive<Record<string, Draft>>({})
function draftFor(provider: string): Draft {
  return (drafts[provider] ??= { clientId: '', clientSecret: '', saving: false })
}

onMounted(async () => {
  try {
    await store.fetchAll()
  } catch {
    // loadError surfaces in the template; nothing to do here.
  }
})

async function save(provider: string): Promise<void> {
  const draft = draftFor(provider)
  if (!draft.clientId.trim() && !draft.clientSecret.trim()) {
    message.error('Enter a client ID and/or secret to save')
    return
  }
  draft.saving = true
  try {
    await store.saveClient(provider, draft.clientId.trim(), draft.clientSecret.trim())
    draft.clientId = ''
    draft.clientSecret = ''
    message.success('Credentials saved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save credentials')
  } finally {
    draft.saving = false
  }
}

function connect(provider: string): void {
  const path = CONNECT_PATHS[provider]
  if (path) window.open(path, '_blank', 'noopener')
}

async function disconnect(provider: string): Promise<void> {
  try {
    await store.disconnect(provider)
    message.success('Disconnected')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to disconnect')
  }
}

const CONNECTED_PILL = 'bg-brand-primary/15 text-brand-primary font-semibold'
const DISCONNECTED_PILL = 'bg-border-divider text-ink-muted font-semibold'
const ROW_CLASS = 'flex min-h-9 flex-wrap items-center justify-between gap-x-4 border-t border-border-divider py-1.5'
const OUTLINE_BUTTON =
  'min-h-11 rounded-lg border border-border-control px-3 text-[13px] text-ink hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-9'

function idPlaceholder(p: CalendarProviderStatus): string {
  return p.client_configured ? 'Client ID is set — enter a new value to replace it' : 'Google OAuth client ID'
}
function secretPlaceholder(p: CalendarProviderStatus): string {
  return p.client_configured ? 'Secret is set — enter a new value to replace it' : 'Google OAuth client secret'
}
</script>

<template>
  <RouteShell>
    <section class="mx-auto flex max-w-3xl flex-col gap-5 pb-6">
      <header class="flex flex-col gap-1.5">
        <h1 class="page-title">Calendar</h1>
        <p class="m-0 text-ink-muted">
          The connection the calendar heartbeat polls with. Client credentials are stored encrypted and write-only: the
          page shows whether each value is set, never the value itself.
        </p>
      </header>

      <div v-if="!store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <div v-else-if="store.loadError" class="text-sm text-status-alert" data-testid="calendar-load-error">
        Failed to load calendar credentials: {{ store.loadError }}
      </div>

      <NEmpty v-else-if="store.providers.length === 0" description="No calendar providers available." class="my-12" />

      <ul v-else class="m-0 flex list-none flex-col gap-4 p-0" data-testid="calendar-provider-list">
        <li
          v-for="p in store.providers"
          :key="p.provider"
          class="flex max-w-[560px] flex-col gap-2.5 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
          :data-testid="`calendar-provider-${p.provider}`"
        >
          <div class="flex items-center gap-2">
            <span class="h-2 w-2 rounded-full bg-life" aria-hidden="true" />
            <h2 class="m-0 flex-1 text-[15px] font-semibold text-ink">{{ p.label }} connection</h2>
            <StatusPill
              :label="p.connected ? 'Connected' : 'Not connected'"
              :color-class="p.connected ? CONNECTED_PILL : DISCONNECTED_PILL"
              :data-testid="`calendar-status-${p.provider}`"
            />
          </div>

          <dl class="m-0 flex flex-col text-sm">
            <div :class="ROW_CLASS">
              <dt class="text-ink-muted">OAuth client</dt>
              <dd class="m-0 font-mono text-xs text-ink">{{ p.client_configured ? 'Set' : 'Not set' }}</dd>
            </div>
            <div v-if="p.scope" :class="ROW_CLASS">
              <dt class="text-ink-muted">Scope</dt>
              <dd class="m-0 min-w-0 truncate font-mono text-xs text-ink" :title="p.scope">{{ p.scope }}</dd>
            </div>
            <div v-if="p.access_token_expires_at" :class="ROW_CLASS">
              <dt class="text-ink-muted">Token expires</dt>
              <dd class="m-0 font-mono text-xs text-ink" :title="formatAbsoluteDate(p.access_token_expires_at)">
                {{ formatDate(p.access_token_expires_at, now) }}
              </dd>
            </div>
            <div v-if="p.last_polled_at" :class="ROW_CLASS">
              <dt class="text-ink-muted">Last successful poll</dt>
              <dd class="m-0 font-mono text-xs text-ink" :title="formatAbsoluteDate(p.last_polled_at)">
                {{ formatDate(p.last_polled_at, now) }}
              </dd>
            </div>
          </dl>

          <p
            v-if="p.last_poll_error"
            class="m-0 rounded-lg border border-status-alert/40 px-2.5 py-1.5 text-xs text-status-alert"
            :data-testid="`calendar-poll-error-${p.provider}`"
          >
            Last poll failed: {{ p.last_poll_error }}
          </p>

          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              :class="OUTLINE_BUTTON"
              :disabled="!p.client_configured"
              :title="p.client_configured ? '' : 'Set the client ID and secret first'"
              :data-testid="`calendar-connect-${p.provider}`"
              @click="connect(p.provider)"
            >
              {{ p.connected ? 'Reconnect' : 'Connect' }} →
            </button>
            <button
              type="button"
              :class="OUTLINE_BUTTON"
              :data-testid="`calendar-refresh-${p.provider}`"
              @click="store.fetchAll()"
            >
              Refresh status
            </button>
            <NPopconfirm
              v-if="p.connected"
              positive-text="Disconnect"
              negative-text="Cancel"
              :positive-button-props="{ type: 'error' }"
              @positive-click="disconnect(p.provider)"
            >
              <template #trigger>
                <button
                  type="button"
                  :class="[OUTLINE_BUTTON, 'text-status-alert']"
                  :data-testid="`calendar-disconnect-${p.provider}`"
                >
                  Disconnect
                </button>
              </template>
              Disconnect {{ p.label }}? This clears the stored tokens; the heartbeat stops until you reconnect. Your
              client ID/secret stay configured.
            </NPopconfirm>
          </div>
          <p class="m-0 text-xs text-ink-faint">
            Connect opens Google's consent screen in a new tab. After granting access, return here and hit “Refresh
            status”.
          </p>

          <div class="mt-1 flex flex-col gap-2.5 border-t border-border-divider pt-3">
            <h3 class="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">OAuth client</h3>
            <label class="flex flex-col gap-1 text-[13px] text-ink-muted">
              Client ID
              <NInput
                v-model:value="draftFor(p.provider).clientId"
                :placeholder="idPlaceholder(p)"
                :disabled="draftFor(p.provider).saving"
                :data-testid="`calendar-client-id-${p.provider}`"
              />
            </label>
            <label class="flex flex-col gap-1 text-[13px] text-ink-muted">
              Client secret
              <NInput
                v-model:value="draftFor(p.provider).clientSecret"
                type="password"
                show-password-on="click"
                :placeholder="secretPlaceholder(p)"
                :disabled="draftFor(p.provider).saving"
                :data-testid="`calendar-client-secret-${p.provider}`"
              />
            </label>
            <NButton
              type="primary"
              class="self-start"
              :loading="draftFor(p.provider).saving"
              :data-testid="`calendar-save-${p.provider}`"
              @click="save(p.provider)"
              >Save credentials</NButton
            >
          </div>
        </li>
      </ul>
    </section>
  </RouteShell>
</template>
